/**
 * ==== STANDALONE:FARMHAND_LITE:START ====
 * Feature: Feed calculator for trip planning and feed needs
 * Tier: Lite ($3/mo)
 * Dependencies: Animal data, FeedBin tracking
 * Generalization:
 *   - Replace bridgeClubAPI with standalone farmhand API
 *   - Remove RB store/shopping integration
 *   - Keep: animal consumption calculation, days remaining, quick calc
 * ============================================
 */
import React, { useState, useEffect } from 'react';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './FeedCalculator.css';

interface Animal {
  id: number;
  name: string;
  animal_type: string;
  category: string;
  count: number;
  daily_consumption_lbs: number;
  feed_type: string;
}

interface FeedBin {
  id: number;
  name: string;
  feed_category: string;
  feed_type: string;
  feed_name: string | null;
  current_lbs: number;
  total_daily_consumption: number;
  days_remaining: number | null;
  status: string;
  bag_size: number;
}

interface CalculatorData {
  animals: Animal[];
  feed_bins: FeedBin[];
  total_daily_consumption_lbs: number;
}

interface ShortfallItem {
  feedType: string;
  feedName: string | null;
  binName: string;
  currentLbs: number;
  daysHave: number;
  daysNeeded: number;
  dailyConsumption: number;
  shortfallLbs: number;
  bagsNeeded: number;
  bagSize: number;
  covered: boolean;
}

const FeedCalculator: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [mode, setMode] = useState<'quick' | 'trip'>('quick');
  const [data, setData] = useState<CalculatorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tripDaysInput, setTripDaysInput] = useState<string>('14');
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [showAllInventory, setShowAllInventory] = useState(false);

  // Quick calc state
  const [quickAnimalType, setQuickAnimalType] = useState<string>('');
  const [quickCount, setQuickCount] = useState<string>('');
  const [quickFeedAmount, setQuickFeedAmount] = useState<string>('');
  const [quickResults, setQuickResults] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await bridgeClubAPI.getFeedCalculatorData();
      setData(result);
    } catch (err) {
      console.error('Failed to load feed calculator data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Parse trip days - default to 14 if empty or invalid
  const tripDays = parseInt(tripDaysInput) || 0;

  // Calculate shortfall for each feed bin
  const calculateShortfall = (): ShortfallItem[] => {
    if (!data || tripDays <= 0) return [];

    return data.feed_bins.map(bin => {
      const daysHave = bin.days_remaining || 0;
      const dailyConsumption = bin.total_daily_consumption || 0;
      const covered = daysHave >= tripDays;
      const shortfallDays = Math.max(0, tripDays - daysHave);
      const shortfallLbs = shortfallDays * dailyConsumption;

      // Use actual bag size from API (from receipt/formula data)
      const bagSize = bin.bag_size || 40;
      const bagsNeeded = shortfallLbs > 0 ? Math.ceil(shortfallLbs / bagSize) : 0;

      return {
        feedType: bin.feed_type || bin.feed_category || 'Feed',
        feedName: bin.feed_name,
        binName: bin.name,
        currentLbs: bin.current_lbs,
        daysHave,
        daysNeeded: tripDays,
        dailyConsumption,
        shortfallLbs,
        bagsNeeded,
        bagSize,
        covered,
      };
    });
  };

  // Quick calc animal types
  const QUICK_ANIMAL_TYPES = [
    { id: 'poultry_layer', name: 'Laying Hens', lbsPerDay: 0.25 },
    { id: 'poultry_broiler', name: 'Meat Birds', lbsPerDay: 0.35 },
    { id: 'swine', name: 'Pigs', lbsPerDay: 5.0 },
    { id: 'goat', name: 'Goats', lbsPerDay: 3.0 },
    { id: 'sheep', name: 'Sheep', lbsPerDay: 4.0 },
    { id: 'cattle', name: 'Cattle', lbsPerDay: 25.0 },
    { id: 'horse', name: 'Horses', lbsPerDay: 20.0 },
    { id: 'dog', name: 'Dogs', lbsPerDay: 1.5 },
    { id: 'rabbit', name: 'Rabbits', lbsPerDay: 0.25 },
  ];

  const calculateQuick = () => {
    const animal = QUICK_ANIMAL_TYPES.find(a => a.id === quickAnimalType);
    if (!animal || !quickCount) return;

    const count = parseFloat(quickCount) || 0;
    const feed = parseFloat(quickFeedAmount) || 0;
    const dailyUsage = animal.lbsPerDay * count;
    const daysFromFeed = feed > 0 ? Math.floor(feed / dailyUsage) : 0;

    setQuickResults({
      animal,
      count,
      dailyUsage,
      weeklyUsage: dailyUsage * 7,
      monthlyUsage: dailyUsage * 30,
      daysFromFeed,
    });
  };

  const resetQuick = () => {
    setQuickAnimalType('');
    setQuickCount('');
    setQuickFeedAmount('');
    setQuickResults(null);
  };

  const shortfall = calculateShortfall();
  const itemsNeeded = shortfall.filter(s => !s.covered);
  const hasData = data && (data.feed_bins.length > 0 || data.animals.length > 0);

  // Collapsed view - just a toggle button
  if (!isExpanded) {
    return (
      <button className="feed-calc-toggle" onClick={() => setIsExpanded(true)}>
        <span className="feed-calc-toggle-icon">🧮</span>
        <span className="feed-calc-toggle-text">Feed Calculator</span>
        <span className="feed-calc-toggle-arrow">▼</span>
      </button>
    );
  }

  if (loading) {
    return (
      <div className="feed-calculator">
        <div className="feed-calc-header">
          <span className="feed-calc-icon">🧮</span>
          <span className="feed-calc-title">Feed Calculator</span>
          <button className="feed-calc-close" onClick={() => setIsExpanded(false)}>×</button>
        </div>
        <div className="feed-calc-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="feed-calculator">
      <div className="feed-calc-header">
        <span className="feed-calc-icon">🧮</span>
        <span className="feed-calc-title">Feed Calculator</span>
        <button className="feed-calc-close" onClick={() => setIsExpanded(false)}>×</button>
      </div>

      {/* Mode Toggle */}
      <div className="calc-mode-toggle">
        <button
          className={`mode-btn ${mode === 'trip' ? 'active' : ''}`}
          onClick={() => { setMode('trip'); setShowShoppingList(false); }}
        >
          Trip Planner
        </button>
        <button
          className={`mode-btn ${mode === 'quick' ? 'active' : ''}`}
          onClick={() => setMode('quick')}
        >
          Quick Calc
        </button>
      </div>

      {/* Trip Planner Mode */}
      {mode === 'trip' && (
        <>
          {!hasData ? (
            <div className="calc-no-data">
              <p>Set up your feed bins first to use Trip Planner.</p>
              <p className="calc-hint">Go to Feed tab to add bins.</p>
            </div>
          ) : !showShoppingList ? (
            <div className="trip-planner">
              <div className="trip-days-input">
                <label>How many days do you need covered?</label>
                <div className="days-input-row">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={tripDaysInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow empty or numeric values only
                      if (val === '' || /^\d+$/.test(val)) {
                        setTripDaysInput(val);
                      }
                    }}
                    onBlur={() => {
                      // On blur, ensure valid value (min 1)
                      const num = parseInt(tripDaysInput);
                      if (!num || num < 1) {
                        setTripDaysInput('1');
                      } else if (num > 365) {
                        setTripDaysInput('365');
                      }
                    }}
                    placeholder="14"
                  />
                  <span className="days-label">days</span>
                </div>
              </div>

              <div className="inventory-section">
                <h4>Current Inventory</h4>
                {(() => {
                  const bins = data?.feed_bins || [];
                  const displayedBins = showAllInventory ? bins : bins.slice(0, 3);
                  return (
                    <>
                      {displayedBins.map(bin => {
                        const s = shortfall.find(x => x.binName === bin.name);
                        return (
                          <div key={bin.id} className={`inventory-item ${s?.covered ? 'covered' : 'shortfall'}`}>
                            <div className="inventory-info">
                              <span className="inventory-name">{bin.name}</span>
                              <span className="inventory-amount">
                                {bin.current_lbs.toFixed(0)} lbs ({bin.days_remaining || 0} days)
                              </span>
                            </div>
                            <span className={`inventory-status ${s?.covered ? 'ok' : 'needs'}`}>
                              {s?.covered ? '✓' : `Need ${s?.shortfallLbs?.toFixed(0) || 0} lbs`}
                            </span>
                          </div>
                        );
                      })}
                      {bins.length > 3 && !showAllInventory && (
                        <button
                          className="show-more-btn"
                          onClick={() => setShowAllInventory(true)}
                        >
                          Show {bins.length - 3} more...
                        </button>
                      )}
                      {showAllInventory && bins.length > 3 && (
                        <button
                          className="show-more-btn"
                          onClick={() => setShowAllInventory(false)}
                        >
                          Show less
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>

              {itemsNeeded.length > 0 && (
                <button
                  className="shopping-list-btn"
                  onClick={() => setShowShoppingList(true)}
                >
                  Build Shopping List ({itemsNeeded.length} items)
                </button>
              )}

              {itemsNeeded.length === 0 && data?.feed_bins.length > 0 && (
                <div className="all-covered">
                  <span className="covered-icon">✓</span>
                  <span>You're covered for {tripDays} days!</span>
                </div>
              )}
            </div>
          ) : (
            <div className="shopping-list">
              <div className="shopping-header">
                <h4>Shopping List for {tripDays}-Day Trip</h4>
                <button
                  className="back-btn"
                  onClick={() => setShowShoppingList(false)}
                >
                  ← Back
                </button>
              </div>

              {itemsNeeded.map((item, idx) => (
                <div key={idx} className="shopping-item">
                  <div className="shopping-item-header">
                    <span className="shopping-feed-type">
                      {item.feedName || item.feedType}
                    </span>
                    {item.feedName && (
                      <span className="shopping-bin-name">({item.binName})</span>
                    )}
                  </div>
                  <div className="shopping-item-details">
                    <span className="shopping-need">Need: {item.shortfallLbs.toFixed(0)} lbs</span>
                    <span className="shopping-bags">
                      → Buy {item.bagsNeeded} × {item.bagSize} lb bag{item.bagsNeeded > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              ))}

              <div className="shopping-total">
                <span>Total: {itemsNeeded.reduce((sum, i) => sum + i.bagsNeeded, 0)} bags</span>
              </div>

              <div className="shopping-actions">
                <button
                  className="action-btn"
                  onClick={() => {
                    const text = `Shopping List for ${tripDays}-Day Trip\n\n` +
                      itemsNeeded.map(i =>
                        `${i.feedName || i.feedType}: ${i.bagsNeeded} × ${i.bagSize} lb bag${i.bagsNeeded > 1 ? 's' : ''}`
                      ).join('\n') +
                      `\n\nTotal: ${itemsNeeded.reduce((sum, i) => sum + i.bagsNeeded, 0)} bags`;
                    navigator.clipboard.writeText(text);
                    alert('Copied to clipboard!');
                  }}
                >
                  📋 Copy List
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Quick Calc Mode */}
      {mode === 'quick' && (
        <div className="quick-calc">
          {!quickResults ? (
            <div className="quick-calc-form">
              <div className="quick-field">
                <label>Animal Type</label>
                <select
                  value={quickAnimalType}
                  onChange={(e) => setQuickAnimalType(e.target.value)}
                >
                  <option value="">Select type...</option>
                  {QUICK_ANIMAL_TYPES.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name} (~{type.lbsPerDay} lbs/day each)
                    </option>
                  ))}
                </select>
              </div>

              <div className="quick-field">
                <label>Number of Animals</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={quickCount}
                  onChange={(e) => setQuickCount(e.target.value)}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val >= 1) {
                      setQuickCount(String(val));
                    } else {
                      setQuickCount('');
                    }
                  }}
                  placeholder="e.g., 25"
                />
              </div>

              <div className="quick-field">
                <label>Feed on Hand (lbs) - optional</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={quickFeedAmount}
                  onChange={(e) => setQuickFeedAmount(e.target.value)}
                  onBlur={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val >= 0) {
                      setQuickFeedAmount(String(val));
                    } else {
                      setQuickFeedAmount('');
                    }
                  }}
                  placeholder="e.g., 500"
                />
              </div>

              <button
                className="quick-calc-btn"
                onClick={calculateQuick}
                disabled={!quickAnimalType || !quickCount}
              >
                Calculate
              </button>
            </div>
          ) : (
            <div className="quick-results">
              <div className="result-header">
                <span>{quickResults.count} {quickResults.animal.name}</span>
              </div>

              <div className="result-grid">
                <div className="result-item">
                  <span className="result-value">{quickResults.dailyUsage.toFixed(1)}</span>
                  <span className="result-label">lbs/day</span>
                </div>
                <div className="result-item">
                  <span className="result-value">{quickResults.weeklyUsage.toFixed(0)}</span>
                  <span className="result-label">lbs/week</span>
                </div>
                <div className="result-item">
                  <span className="result-value">{quickResults.monthlyUsage.toFixed(0)}</span>
                  <span className="result-label">lbs/month</span>
                </div>
              </div>

              {quickResults.daysFromFeed > 0 && (
                <div className="result-duration">
                  <span className="duration-icon">📦</span>
                  <span className="duration-text">
                    {quickFeedAmount} lbs will last <strong>~{quickResults.daysFromFeed} days</strong>
                  </span>
                </div>
              )}

              <button className="quick-reset-btn" onClick={resetQuick}>
                Calculate Again
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedCalculator;
